/**
 * Configuración del Firebase Web SDK.
 *
 * Todas las claves se leen desde variables de entorno con prefijo `VITE_`
 * (definidas en `.env.local`, ver `.env.example`). Estas claves son públicas
 * por diseño (el SDK Web las usa en el navegador); la protección real se
 * configura en las reglas de seguridad de Realtime Database.
 *
 * NOTA: App Check está desactivado por ahora. Cuando se active, agregar:
 *   - import desde 'firebase/app-check'
 *   - inicialización con ReCaptchaEnterpriseProvider
 *   - validar VITE_FIREBASE_APPCHECK_SITE_KEY obligatorio en prod
 * Hasta entonces, /leads queda protegido solo por las reglas RTDB.
 */
import type { Analytics } from 'firebase/analytics';
import { initializeApp, getApps, getApp, type FirebaseApp } from 'firebase/app';
import { getDatabase, type Database } from 'firebase/database';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app: FirebaseApp = getApps().length ? getApp() : initializeApp(firebaseConfig);
export const db: Database = getDatabase(app);

/**
 * Analytics se importa dinámicamente para no inflar el bundle inicial. Esto
 * mueve `firebase/analytics` (~50KB) a un chunk separado que solo se carga
 * cuando el navegador puede iniciarlo (evita SSR y jsdom).
 */
export const analyticsPromise: Promise<Analytics | null> =
  typeof window !== 'undefined'
    ? import('firebase/analytics')
        .then(async ({ getAnalytics, isSupported }) => {
          const ok = await isSupported();
          return ok ? getAnalytics(app) : null;
        })
        .catch(() => null)
    : Promise.resolve(null);

export default app;
